import BlurredBackground from '$tablet-app/src/app/components/BlurredBackground';
import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { palette } from '$tablet-app/src/app/theme';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { VideoTutorialData } from '@akin-home/video-tutorials';
import React, { FC, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { Overlay, OverlayProps, Slider } from 'react-native-elements';
import styled from 'styled-components/native';
import Video from '../Video';
import { VideoProgress } from '../Video/video_props';

interface Props extends OverlayProps {
  video_data: VideoTutorialData;

  onClose?: () => void;

  controls?: boolean;
}

const FullScreenVideo: FC<Props> = (props) => {
  const { video_data, onClose, ...overlay_props } = props;
  const [paused, pause, play] = useBool();

  /** In seconds */
  const [progress, setProgress] = useState<number>(0);

  /** In seconds */
  const [duration, setDuration] = useState<number>(0);

  const ref = useRef<any>(undefined);
  const { height, width } = useWindowDimensions();

  const onPressPlayPause = () => {
    paused ? play() : pause();
  };

  const onLoad = (data: any) => {
    setDuration(data.duration);
  };

  const onProgress = (data: VideoProgress) => {
    setProgress(Math.floor(data.currentTime));
  };

  const onChangeProgress = (value: number) => {
    ref.current?.seek(value);
  };

  return (
    <Overlay
      animationType="fade"
      transparent
      overlayStyle={{ height, width, backgroundColor: 'rgba(5, 22, 51, 0.2)' }}
      {...overlay_props}
    >
      <BlurredBackground />

      <Container>
        <CenterColumn>
          <Video
            ref={ref}
            style={{
              borderRadius: 20,
              height: 564,
              width: 1000,
            }}
            height={564}
            width={1000}
            paused={paused}
            video_data={video_data}
            onProgress={onProgress}
            onLoad={onLoad}
          />

          {/* If video is youtube hosted, we will just use the youtube player controls for now. */}
          {!video_data.youtube_id ? (
            <Controls>
              <PlayPauseButton
                onPress={onPressPlayPause}
                icon="play"
                size="xlarge"
                iconColor={palette.primary_70}
              />

              <Spacer width="24px" />

              <CenterControls>
                {video_data.title ? (
                  <Title weight="semibold">{video_data.title}</Title>
                ) : null}

                <SliderRow>
                  <TimeText>{secondsToMinuteString(progress)}</TimeText>
                  <Spacer width="8px" />
                  <Slider
                    style={{ flex: 1 }}
                    value={progress}
                    onValueChange={onChangeProgress}
                    onSlidingStart={pause}
                    onSlidingComplete={play}
                    maximumValue={duration}
                  />
                  <Spacer width="8px" />
                  <TimeText>{secondsToMinuteString(duration)}</TimeText>
                </SliderRow>
              </CenterControls>
            </Controls>
          ) : null}
        </CenterColumn>

        <CloseButton
          variant="secondary"
          size="xlarge"
          icon="times"
          iconColor="white"
          onPress={onClose}
        />
      </Container>
    </Overlay>
  );
};

const secondsToMinuteString = (num: number) => {
  const minute = Math.floor(num / 60);
  const seconds = Math.floor(num % 60);

  const minute_string = minute > 10 ? `${minute}` : `0${minute}`;
  const second_string = seconds > 10 ? `${seconds}` : `0${seconds}`;

  return `${minute_string}:${second_string}`;
};

const Controls = styled.View`
  flex-direction: row;

  padding-top: 16px;
`;

const CenterControls = styled.View`
  flex: 1;
`;

const Container = styled.View`
  align-items: center;
  padding-top: 50px;
`;

const CenterColumn = styled.View`
  width: 1000px;
`;

const PlayPauseButton = styled(NewButton)`
  background-color: white;
`;

const CloseButton = styled(NewButton)`
  position: absolute;
  top: 16px;
  right: 10px;
`;

const StyledVideo = styled(Video)`
  height: 564px;
  width: 1000px;

  border-radius: 20px;
`;

const Title = styled(Text)`
  color: white;
`;

const SliderRow = styled.View`
  flex-direction: row;

  align-items: center;
`;

const TimeText = styled(Text)`
  color: white;
`;

export default FullScreenVideo;
